import {
  Logout as LogoutIcon,
  LightMode,
  DarkMode,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useRevenueCat } from "../contexts/RevenueCatContext";
import { logger } from "../services";
import { useTheme } from "../theme";

const TopBar: React.FC = () => {
  const { user, logout } = useAuth();
  const { isPlusPlan } = useRevenueCat();
  const { toggleDarkMode, isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
      handleUserMenuClose();
    } catch (error) {
      await logger.error("logout_failed", error as Error);
    }
  };

  const handleThemeToggle = async () => {
    toggleDarkMode();
  };

  const handleUpgradeClick = async () => {
    await logger.userAction("upgrade_click", {
      source: "top_bar",
    });
    navigate("/subscription");
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: 1201,
        backgroundColor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        color: "text.primary",
      }}
    >
      <Toolbar sx={{ minHeight: "48px !important", py: 0.5 }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
          FitCheckr
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {user && (
            <>
              {!isPlusPlan && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUpgradeClick}
                  sx={{
                    borderRadius: 1,
                    py: 0.25,
                    px: 1.5,
                    mr: 1,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    minHeight: "28px",
                    textTransform: "none",
                    color: "white",
                    background: "linear-gradient(45deg, #1976D2 30%, #1565C0 90%)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #1565C0 30%, #0D47A1 90%)",
                    },
                  }}
                >
                  Upgrade to Plus
                </Button>
              )}
              <IconButton
                onClick={handleThemeToggle}
                size="small"
                sx={{
                  p: 0.25,
                  mr: 1,
                  color: "text.primary",
                }}
              >
                {isDarkMode ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
              </IconButton>

              <IconButton
                onClick={handleUserMenuOpen}
                sx={{
                  p: 0.25,
                  color: "text.primary",
                }}
              >
                <Avatar
                  src={user.photoURL || undefined}
                  sx={{ width: 24, height: 24 }}
                >
                  {user.displayName?.[0] || <PersonIcon />}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
